import * as api from '../api';
import { ActionContext } from 'vuex';
import { defineModule, localActionContext } from 'direct-vuex';
import { BaseUser } from '@/models';
import * as Utils from '@/utils';

export interface UserInfo extends BaseUser {
  isSuperAdmin: boolean;
}

export interface AuthState {
  isSignUp: false;
  isAuthenticated: boolean;
  authStatus: AuthStatus;
  user: UserInfo | null;
  failedEmail: string;
  loginEmail: string;
  accessToken: string;
}

export enum AuthStatus {
  READY,
  SUCCESS,
  IN_PROGRESS,
  COOKIES_DISABLED,
  FAILURE
}

const getInitialState = (): AuthState => {
  return {
    isSignUp: false,
    isAuthenticated: false,
    user: null,
    authStatus: AuthStatus.READY,
    failedEmail: '',
    loginEmail: '',
    accessToken: ''
  };
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-use-before-define
const authActionContext = (context: any) => localActionContext(context, auth);

const login = async (
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  context: ActionContext<any, any>,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  loginFn: () => Promise<any>
) => {
  const { commit, state } = authActionContext(context);

  if (state.authStatus === AuthStatus.IN_PROGRESS) {
    console.warn(
      'Attempting login while authentication is already in progress'
    );
  }

  commit.updateStatus(AuthStatus.IN_PROGRESS);

  try {
    // There might be a user here from the getStarted workflow
    await api.auth.signOut();

    // The store update is handled by `signInFirebaseUser` which is
    // triggered when the firebaseUser changes (stored by the firebase
    // auth library).
    //
    // `signInFireBaseUser` sets status = SUCCESS;
    await loginFn();
  } catch (error) {
    console.error(error);

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    if ((error as any).error === 'idpiframe_initialization_failed') {
      commit.updateStatus(AuthStatus.COOKIES_DISABLED);
    } else {
      commit.updateStatus(AuthStatus.FAILURE);
    }
  }
};

const auth = defineModule({
  namespaced: true as true,
  state: getInitialState() as AuthState,
  getters: {},
  mutations: {
    setAccessToken: (state, token: string) => {
      state.accessToken = token;
    },

    setAuthenticated: (state, userInfo: UserInfo) => {
      state.isAuthenticated = true;
      state.user = userInfo;
    },

    setFailedEmail: (state, email: string) => {
      state.failedEmail = email;
    },

    setLoginEmail: (state, email: string) => {
      state.loginEmail = email;
    },

    setUnauthenticated: state => {
      state.isAuthenticated = false;
      state.user = null;
    },

    updateStatus: (state, status: AuthStatus) => {
      state.authStatus = status;
    }
  },
  actions: {
    // Auth happens in two parts:
    // 1. We need to ask google/firebase to authenticate the user and give us back an
    //    id token
    // 2. We need to send that id token to our server to determine which user it belongs
    //    to. Currently we do that by matching on email.
    //
    // However, we always first check if firebase has stored an idToken from
    // the last session so sometimes we only need to do step #2 (the existing token check
    // happens in main.ts)
    async loginWithGoogle(context, idToken) {
      login(context, () => api.auth.signInWithGoogle(idToken));
    },

    async loginWithMicrosoft(context) {
      login(context, () => api.auth.signInWithMicrosoft());
    },

    async requestEmailLoginCode(context, email: string) {
      const { commit } = authActionContext(context);
      commit.updateStatus(AuthStatus.IN_PROGRESS);

      try {
        await api.auth.requestLoginCode(email);
        commit.setLoginEmail(email);
        commit.updateStatus(AuthStatus.READY);
      } catch (error) {
        console.error(error);
        commit.updateStatus(AuthStatus.FAILURE);
      }
    },

    async loginWithEmailCode(
      context,
      { email, code }: { email: string; code: string }
    ) {
      const { commit, dispatch } = authActionContext(context);
      commit.updateStatus(AuthStatus.IN_PROGRESS);

      try {
        const tokens = await api.auth.exchangeEmailCodeForToken(email, code);
        localStorage.setItem('unrulr_refresh_token', tokens.refreshToken);
        dispatch.signInWithAccessToken(tokens.accessToken);
      } catch (error) {
        console.error(error);
        commit.updateStatus(AuthStatus.FAILURE);
      }
    },

    async loginWithMagicLink(context, token: string) {
      const { commit, dispatch } = authActionContext(context);
      commit.updateStatus(AuthStatus.IN_PROGRESS);

      try {
        const tokens = await api.auth.exchangeMagicLinkForToken(token);
        localStorage.setItem('unrulr_refresh_token', tokens.refreshToken);
        dispatch.signInWithAccessToken(tokens.accessToken);
      } catch (error) {
        console.error(error);
        commit.updateStatus(AuthStatus.FAILURE);
      }
    },

    // Attempt login
    // - Does not error if there is no refresh token
    // - Does error if refresh token is invalid/fails
    async loginWithRefreshToken(context) {
      const refreshToken = localStorage.getItem('unrulr_refresh_token');
      Utils.log({ refreshToken });

      if (!refreshToken) {
        // No refresh token, no action
        return;
      }

      const { commit, dispatch } = authActionContext(context);
      commit.updateStatus(AuthStatus.IN_PROGRESS);

      try {
        const tokens = await api.auth.exchangeRefreshToken(refreshToken);
        localStorage.setItem('unrulr_refresh_token', tokens.refreshToken);
        dispatch.signInWithAccessToken(tokens.accessToken);
      } catch (error) {
        console.error(error);
        localStorage.removeItem('unrulr_refresh_token');
        commit.updateStatus(AuthStatus.FAILURE);
      }
    },

    async setUserName(context, name: string) {
      const { commit, state } = authActionContext(context);

      if (!state.user) {
        throw new Error('Cannot set user name without an authenticated user');
      }

      commit.updateStatus(AuthStatus.IN_PROGRESS);

      try {
        await api.auth.updateName(state.user.userId, name);
        const userInfo = await api.auth.userInfo();
        commit.setAuthenticated(userInfo);
        commit.updateStatus(AuthStatus.SUCCESS);
      } catch (error) {
        console.error(error);
        commit.updateStatus(AuthStatus.FAILURE);
      }
    },

    async signInWithAccessToken(context, accessToken: string) {
      const { commit } = authActionContext(context);

      try {
        api.auth.setAccessToken(accessToken);
        commit.setAccessToken(accessToken);

        const userInfo = await api.auth.userInfo();

        commit.setAuthenticated(userInfo);
        commit.updateStatus(AuthStatus.SUCCESS);
      } catch (error) {
        console.error(error);

        commit.updateStatus(AuthStatus.FAILURE);

        api.auth.signOut();
      }
    },

    // Listens to the firebase user state.  Connected in main.ts
    async signInFirebaseUser(context, user: firebase.User) {
      const { commit, dispatch } = authActionContext(context);

      // We set IN_PROGRESS here because sometimes we skip the login() call
      // (see login() notes).
      commit.updateStatus(AuthStatus.IN_PROGRESS);

      try {
        const idToken = await user.getIdToken();

        Utils.log({ idToken });

        const authTokens = await api.auth.exchangeIdToken(idToken, 'dashboard');

        dispatch.signInWithAccessToken(authTokens.accessToken);
        localStorage.setItem('unrulr_refresh_token', authTokens.refreshToken);

        api.auth.signOutFirebase();
      } catch (error) {
        console.error(error);

        commit.setFailedEmail(user.email || 'Unknown email');
        commit.updateStatus(AuthStatus.FAILURE);

        api.auth.signOut();
      }
    },

    async logout(context) {
      const { commit } = authActionContext(context);

      await api.auth.signOut();
      localStorage.removeItem('unrulr_refresh_token');

      commit.setUnauthenticated();
      context.dispatch('org/clear', null, { root: true });
    }
  }
});

export default auth;
